import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Crown, Sparkles, Shield, Bell, Moon, Sun, Trash2, BarChart3 } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { ThemeToggle } from '../components/ThemeToggle';
import { PremiumBadge } from '../components/PremiumBadge';
import { ConfirmModal } from '../components/ConfirmModal';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
export function SettingsPage() {
  const navigate = useNavigate();
  const {
    tier
  } = useSubscription();
  const {
    theme
  } = useTheme();
  const {
    logout
  } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const handleDeleteAccount = async () => {
    // In production, this would call your backend API
    console.log('Deleting account...');
    await logout();
    navigate('/login');
  };
  return <>
      <div className="min-h-screen bg-game-bg-pink dark:bg-[#1a1d29] pb-24" data-id="element-1152">
        <header className="px-6 pt-6 pb-4 sticky top-0 bg-game-bg-pink/90 dark:bg-[#1a1d29]/90 backdrop-blur-sm z-10" data-id="element-1153">
          <div className="flex items-center gap-4" data-id="element-1154">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="!p-2" data-id="element-1155">
              <ArrowLeft className="w-6 h-6" data-id="element-1156" />
            </Button>
            <h1 className="text-2xl font-fredoka font-bold text-game-dark dark:text-white" data-id="element-1157">
              Settings
            </h1>
          </div>
        </header>

        <main className="px-6 space-y-6 max-w-2xl mx-auto" data-id="element-1158">
          {/* Subscription Section */}
          <section data-id="element-1159">
            <h2 className="text-lg font-fredoka font-bold text-game-dark dark:text-white mb-4" data-id="element-1160">
              Subscription
            </h2>

            {tier === 'free' ? <Card className="p-6 bg-gradient-to-br from-game-purple via-game-teal to-game-coral text-white relative overflow-hidden" data-id="element-1161">
                <div className="relative z-10" data-id="element-1162">
                  <div className="flex items-start justify-between mb-4" data-id="element-1163">
                    <div className="flex items-center gap-3" data-id="element-1164">
                      <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center" data-id="element-1165">
                        <Crown className="w-6 h-6 text-white" data-id="element-1166" />
                      </div>
                      <div data-id="element-1167">
                        <h3 className="text-xl font-fredoka font-bold" data-id="element-1168">
                          Upgrade to Premium
                        </h3>
                        <p className="text-sm opacity-90 font-quicksand" data-id="element-1169">
                          Unlock unlimited features
                        </p>
                      </div>
                    </div>
                  </div>
                  <ul className="space-y-2 mb-6 text-sm font-quicksand" data-id="element-1170">
                    <li className="flex items-center gap-2" data-id="element-1171">
                      <Sparkles className="w-4 h-4" data-id="element-1172" />
                      <span data-id="element-1173">Unlimited quests & collaborations</span>
                    </li>
                    <li className="flex items-center gap-2" data-id="element-1174">
                      <Sparkles className="w-4 h-4" data-id="element-1175" />
                      <span data-id="element-1176">Advanced focus modes & AI features</span>
                    </li>
                    <li className="flex items-center gap-2" data-id="element-1177">
                      <Sparkles className="w-4 h-4" data-id="element-1178" />
                      <span data-id="element-1179">Custom themes & data export</span>
                    </li>
                  </ul>
                  <button onClick={() => navigate('/upgrade')} className="w-full bg-white text-game-purple hover:bg-gray-100 px-6 py-3 rounded-xl font-fredoka font-bold transition-colors flex items-center justify-center gap-2" data-id="element-1180">
                    <Crown className="w-5 h-5" data-id="element-1181" />
                    Upgrade Now
                  </button>
                </div>
                <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl" data-id="element-1182"></div>
                <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white/10 rounded-full blur-2xl" data-id="element-1183"></div>
              </Card> : <Card className="p-6" data-id="element-1184">
                <div className="flex items-start justify-between mb-4" data-id="element-1185">
                  <div className="flex items-center gap-3" data-id="element-1186">
                    <div className="w-12 h-12 bg-gradient-to-br from-game-yellow to-game-coral rounded-2xl flex items-center justify-center" data-id="element-1187">
                      <Crown className="w-6 h-6 text-white" data-id="element-1188" />
                    </div>
                    <div data-id="element-1189">
                      <div className="flex items-center gap-2 mb-1" data-id="element-1190">
                        <h3 className="text-xl font-fredoka font-bold text-game-dark dark:text-white" data-id="element-1191">
                          Premium Plan
                        </h3>
                        <PremiumBadge size="sm" data-id="element-1192" />
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-1193">
                        Active subscription
                      </p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3" data-id="element-1194">
                  <div className="flex justify-between text-sm font-quicksand" data-id="element-1195">
                    <span className="text-gray-600 dark:text-gray-400" data-id="element-1196">
                      Status
                    </span>
                    <span className="font-bold text-game-teal" data-id="element-1197">Active</span>
                  </div>
                  <div className="flex justify-between text-sm font-quicksand" data-id="element-1198">
                    <span className="text-gray-600 dark:text-gray-400" data-id="element-1199">
                      Plan
                    </span>
                    <span className="font-bold text-game-dark dark:text-white" data-id="element-1200">
                      Premium
                    </span>
                  </div>
                  <Button variant="secondary" className="w-full mt-4" onClick={() => {
                // Handle manage subscription
                console.log('Manage subscription');
              }} data-id="element-1201">
                    Manage Subscription
                  </Button>
                </div>
              </Card>}
          </section>

          {/* Analytics */}
          <section data-id="element-1202">
            <h2 className="text-lg font-fredoka font-bold text-game-dark dark:text-white mb-4" data-id="element-1203">
              Analytics
            </h2>
            <Card className="p-6" data-id="element-1204">
              <button onClick={() => navigate('/analytics')} className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl transition-colors" data-id="element-1205">
                <div className="flex items-center gap-3" data-id="element-1206">
                  <BarChart3 className="w-5 h-5 text-gray-600 dark:text-gray-400" data-id="element-1207" />
                  <div className="text-left" data-id="element-1208">
                    <p className="font-fredoka font-medium text-game-dark dark:text-white" data-id="element-1209">
                      Advanced Analytics
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-1210">
                      View detailed insights {tier === 'free' && '(Premium)'}
                    </p>
                  </div>
                </div>
                <ArrowLeft className="w-5 h-5 text-gray-400 rotate-180" data-id="element-1211" />
              </button>
            </Card>
          </section>

          {/* Appearance */}
          <section data-id="element-1212">
            <h2 className="text-lg font-fredoka font-bold text-game-dark dark:text-white mb-4" data-id="element-1213">
              Appearance
            </h2>
            <Card className="p-6" data-id="element-1214">
              <div className="flex items-center justify-between" data-id="element-1215">
                <div className="flex items-center gap-3" data-id="element-1216">
                  {theme === 'dark' ? <Moon className="w-5 h-5 text-gray-600 dark:text-gray-400" data-id="element-1217" /> : <Sun className="w-5 h-5 text-gray-600 dark:text-gray-400" data-id="element-1218" />}
                  <div data-id="element-1219">
                    <p className="font-fredoka font-bold text-game-dark dark:text-white" data-id="element-1220">
                      Dark Mode
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-1221">
                      Toggle dark theme
                    </p>
                  </div>
                </div>
                <ThemeToggle data-id="element-1222" />
              </div>
            </Card>
          </section>

          {/* Notifications */}
          <section data-id="element-1223">
            <h2 className="text-lg font-fredoka font-bold text-game-dark dark:text-white mb-4" data-id="element-1224">
              Notifications
            </h2>
            <Card className="p-6" data-id="element-1225">
              <div className="flex items-center justify-between mb-4" data-id="element-1226">
                <div className="flex items-center gap-3" data-id="element-1227">
                  <Bell className="w-5 h-5 text-gray-600 dark:text-gray-400" data-id="element-1228" />
                  <div data-id="element-1229">
                    <p className="font-fredoka font-bold text-game-dark dark:text-white" data-id="element-1230">
                      Push Notifications
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-1231">
                      Get notified about quest updates
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer" data-id="element-1232">
                  <input type="checkbox" className="sr-only peer" defaultChecked data-id="element-1233" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-game-purple/20 dark:peer-focus:ring-game-purple/40 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-game-purple" data-id="element-1234"></div>
                </label>
              </div>
            </Card>
          </section>

          {/* Privacy */}
          <section data-id="element-1235">
            <h2 className="text-lg font-fredoka font-bold text-game-dark dark:text-white mb-4" data-id="element-1236">
              Privacy & Security
            </h2>
            <Card className="p-6 space-y-4" data-id="element-1237">
              <button onClick={() => navigate('/privacy')} className="w-full flex items-center justify-between p-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl transition-colors" data-id="element-1238">
                <div className="flex items-center gap-3" data-id="element-1239">
                  <Shield className="w-5 h-5 text-gray-600 dark:text-gray-400" data-id="element-1240" />
                  <span className="font-fredoka font-medium text-game-dark dark:text-white" data-id="element-1241">
                    Privacy Policy
                  </span>
                </div>
                <ArrowLeft className="w-5 h-5 text-gray-400 rotate-180" data-id="element-1242" />
              </button>
            </Card>
          </section>

          {/* Danger Zone */}
          <section data-id="element-1243">
            <h2 className="text-lg font-fredoka font-bold text-red-600 dark:text-red-400 mb-4" data-id="element-1244">
              Danger Zone
            </h2>
            <Card className="p-6" data-id="element-1245">
              <button onClick={() => setShowDeleteModal(true)} className="w-full flex items-center justify-between p-3 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors" data-id="element-1246">
                <div className="flex items-center gap-3" data-id="element-1247">
                  <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" data-id="element-1248" />
                  <div className="text-left" data-id="element-1249">
                    <p className="font-fredoka font-bold text-red-600 dark:text-red-400" data-id="element-1250">
                      Delete Account
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-1251">
                      Permanently delete your account and data
                    </p>
                  </div>
                </div>
              </button>
            </Card>
          </section>
        </main>
      </div>

      {/* Delete Account Confirmation Modal */}
      <ConfirmModal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} onConfirm={handleDeleteAccount} title="Delete Account?" message="This action cannot be undone. All your quests, progress, and data will be permanently deleted." confirmText="Delete Account" cancelText="Cancel" variant="danger" data-id="element-1252" />
    </>;
}